import { ApolloError, gql, useMutation } from '@apollo/client'
import { useCallback } from 'react'

export const REQUEST_EMAIL_CHANGE_MUTATION = gql`
  mutation RequestEmailChange($input: RequestEmailChangeInput!) {
    requestEmailChange(input: $input) {
      newEmail
    }
  }
`

type RequestEmailChangeMutation = {
  requestEmailChange: { newEmail: string | null } | null
}

type RequestEmailChangeMutationVariables = {
  input: {
    newEmail: string
  }
}

export const RequestEmailChangeErrorKind = {
  BLOCKED_BY_PARTNERSHIP: 'blockedByPartnership',
  INVALID_EMAIL: 'invalidEmail',
  GENERIC: 'generic',
} as const

export type RequestEmailChangeErrorKind =
  (typeof RequestEmailChangeErrorKind)[keyof typeof RequestEmailChangeErrorKind]

export type RequestEmailChangeResult =
  | { success: true }
  | { success: false; kind: RequestEmailChangeErrorKind }

const BLOCKED_MESSAGE = 'You are not allowed to change your email'

function readErrorKey(gqlError: unknown): string | undefined {
  const candidate = gqlError as {
    info?: { key?: string }
    extensions?: { info?: { key?: string } }
  }
  return candidate?.info?.key ?? candidate?.extensions?.info?.key
}

function extractErrorKind(error: unknown): RequestEmailChangeErrorKind {
  if (error instanceof ApolloError) {
    const hasInvalidEmail = error.graphQLErrors.some((gqlError) =>
      readErrorKey(gqlError)?.startsWith('newEmail')
    )
    if (hasInvalidEmail) return RequestEmailChangeErrorKind.INVALID_EMAIL

    const blocked = error.graphQLErrors.some((gqlError) =>
      gqlError.message?.includes(BLOCKED_MESSAGE)
    )
    if (blocked) return RequestEmailChangeErrorKind.BLOCKED_BY_PARTNERSHIP
  }
  return RequestEmailChangeErrorKind.GENERIC
}

export function useRequestEmailChange() {
  const [mutate, { loading }] = useMutation<
    RequestEmailChangeMutation,
    RequestEmailChangeMutationVariables
  >(REQUEST_EMAIL_CHANGE_MUTATION)

  const execute = useCallback(
    async (
      input: RequestEmailChangeMutationVariables['input']
    ): Promise<RequestEmailChangeResult> => {
      try {
        await mutate({ variables: { input } })
        return { success: true }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production')
          console.error('[useRequestEmailChange] mutation failed', err)

        return { success: false, kind: extractErrorKind(err) }
      }
    },
    [mutate]
  )

  return { execute, loading }
}
